export type CmtsRepositoryPagedResult<T> = {
  items: readonly T[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  raw?: unknown | undefined;
};

function collectionRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? value as Record<string, unknown> : null;
}

export function extractItems(value: unknown): readonly unknown[] {
  if (Array.isArray(value)) return value;
  const record = collectionRecord(value);
  if (!record) return [];
  const candidates = [record.items, record.Items, record.rows, record.Rows, record.data, record.Data, record.results, record.Results];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

export function mapCollection<T>(value: unknown, mapper: (item: unknown) => T): readonly T[] {
  return extractItems(value).map(mapper);
}

export function mapPagedResult<T>(value: unknown, mapper: (item: unknown) => T): CmtsRepositoryPagedResult<T> {
  const record = collectionRecord(value) ?? {};
  const items = mapCollection(value, mapper);
  return {
    items,
    pageNumber: readNumber(record.pageNumber ?? record.PageNumber, 1),
    pageSize: readNumber(record.pageSize ?? record.PageSize, items.length),
    totalRecords: readNumber(record.totalRecords ?? record.TotalRecords ?? record.total ?? record.Total, items.length),
    totalPages: readNumber(record.totalPages ?? record.TotalPages, items.length > 0 ? 1 : 0),
    hasPreviousPage: readBoolean(record.hasPreviousPage ?? record.HasPreviousPage, false),
    hasNextPage: readBoolean(record.hasNextPage ?? record.HasNextPage, false),
    raw: value,
  };
}

function readNumber(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return fallback;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return fallback;
}
